import bemBlock from '@gik/core/utils/bemBlock';
import { Grid, HoverAnimation } from '@gik/ui';
import moment from 'moment';
import React from 'react';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';
import Link from '@/components/Link';
import GikLogoWhite from '../assets/gik-logo-white.svg';
import FacebookIconWhite from '../assets/facebook.svg'; // TODO: consider moving to SvgIcon as a Gik collection
import InstagramIconWhite from '../assets/instagram.svg'; // TODO: consider moving to SvgIcon as a Gik collection
import PinterestIconWhite from '../assets/pinterest.svg'; // TODO: consider moving to SvgIcon as a Gik collection
import TwitterIconWhite from '../assets/twitter.svg'; // TODO: consider moving to SvgIcon as a Gik collection
import YoutubeIconWhite from '../assets/youtube.svg'; // TODO: consider moving to SvgIcon as a Gik collection
import { usePostsFeatured } from '@gik/blog/api/wp';
import routes from '@gik/core/routes';
import noop from '@gik/core/utils/noop';
import { CreateClickedLocation, useCreateAnalyticsController } from '@gik/create/hooks/useCreateAnalytics';

const bem = bemBlock('app-footer');

interface FooterLink {
  title: string;
  url: string;
  trackingFunction?: () => void;
}

interface FooterLinkListProps {
  title: string;
  links: FooterLink[];
}

function FooterLinkList({ title, links }: FooterLinkListProps) {
  return (
    <div className={bem('grid-link-list')}>
      <p>{title}</p>
      {links.map(({ title, url, trackingFunction = noop }) => (
        <Link key={url} href={url}>
          <a className={bem('grid-link')}>
            <p onClick={trackingFunction}>{title}</p>
          </a>
        </Link>
      ))}
    </div>
  );
}

interface SocialMediaLink {
  icon: React.ReactNode;
  url: string;
}

const socialMediaLinks: SocialMediaLink[] = [
  { icon: <FacebookIconWhite />, url: 'https://www.facebook.com/giveinkind/' },
  { icon: <TwitterIconWhite />, url: 'https://twitter.com/giveinkind' },
  { icon: <InstagramIconWhite />, url: 'https://www.instagram.com/giveinkind/' },
  { icon: <PinterestIconWhite />, url: 'https://www.pinterest.com/giveinkind' },
  { icon: <YoutubeIconWhite />, url: 'https://www.youtube.com/channel/UCGp15GUbx5bFwxQwAQUpnRw' },
];

export default function AppFooter() {
  const { t } = useTranslation();
  const { trackCreateClicked } = useCreateAnalyticsController();
  const posts = usePostsFeatured('footer-help', 3);

  const footerLinkLists: FooterLinkListProps[] = [
    {
      title: t(translationKeys.inkind),
      links: [
        { title: t(translationKeys.about), url: routes.about },
        { title: t(translationKeys.contact), url: routes.contact },
        { title: t(translationKeys.support), url: routes.support },
        { title: t(translationKeys.press), url: 'https://create.giveinkind.com/press/' },
        { title: t(translationKeys.write), url: 'https://create.giveinkind.com/call-for-submissions/' },
      ],
    },
    {
      title: t(translationKeys.inkindPages),
      links: [
        {
          title: t(translationKeys.create),
          url: routes.create,
          trackingFunction: () => trackCreateClicked(CreateClickedLocation.PAGE_FOOTER),
        },
        { title: t(translationKeys.howItWorks), url: routes.howItWork },
        { title: t(translationKeys.features), url: routes.features },
        { title: t(translationKeys.compare), url: routes.competitors },
        { title: t(translationKeys.faq), url: routes.commonQuestions },
      ],
    },
    {
      title: t(translationKeys.howCanIHelp),
      links: [
        { title: t(translationKeys.browseSituations), url: routes.giveBySituation },
        { title: t(translationKeys.blog), url: routes.blog },
        ...(posts?.data?.map(p => ({ title: p.title.rendered, url: `${routes.blog}/${p.slug}` })) || []),
      ],
    },
  ];

  function FinePrintLink({ title, url }: FooterLink) {
    return (
      <Link href={url}>
        <a>{title}</a>
      </Link>
    );
  }

  const finePrint = (className: string) => (
    <div className={bem('fine-print', undefined, className)}>
      <p>
        {/* TODO: migrate to use LegalTextModal */}
        <FinePrintLink title={t(translationKeys.tos)} url={routes.termsService} /> •{' '}
        <FinePrintLink title={t(translationKeys.privacy)} url={routes.privacyPolicy} />
        {/* TODO: attribution - hidden for now*/
        /*<span className="gik-hidden-lg-down"> • </span>
              <br className="gik-hidden-lg" />
        <FinePrintLink title={t(translationKeys.attribution)} url="/attribution" />*/}
        <br className="xs" />
        &copy;{moment().year()} Give InKind. All Rights Reserved.
      </p>
    </div>
  );

  return (
    <footer className={bem()}>
      {finePrint('gik-hidden-lg')}
      <Grid className={bem('links-grid')}>
        {footerLinkLists.map(list => (
          <FooterLinkList key={list.title} {...list} />
        ))}
      </Grid>
      <div className={bem('right')}>
        <div className={bem('heading')}>
          <GikLogoWhite />
          <div className={bem('social-links')}>
            {socialMediaLinks.map(link => (
              <Link key={link.url} href={link.url}>
                <a rel="nofollow">
                  <HoverAnimation>
                    <div className={bem('social-link')}>{link.icon}</div>
                  </HoverAnimation>
                </a>
              </Link>
            ))}
          </div>
        </div>
        {finePrint('gik-hidden-lg-down')}
      </div>
    </footer>
  );
}
